import { useDataPart } from '@ai-sdk-tools/store';
import type { BrowserStepDataPart } from '@newfront-insurance/ai-api';
import type { ToolUIPart } from 'ai';
import { Maximize2 } from 'lucide-react';
import type { ReactElement } from 'react';
import { useState } from 'react';

import { BrowserLiveView } from './browser-preview/browser-live-view-lazy';
import { useBrowserPreviewSafe } from './browser-preview/browser-preview-provider';
import type { DataPartRendererProps } from '../../lib/default-renderers';
import { getDataPartData } from '../../lib/default-renderers';

// Two React surfaces render browserUse activity:
//
//   1. `BrowserStepLiveView` (data-part renderer). Rendered as soon as a
//      `data-browserStep` event arrives — before the main tool call has any
//      output. This is where the DCV live stream mounts WHILE the sub-agent
//      works. Registered in `createDefaultDataPartRenderers()`.
//
//   2. `BrowserUseTool` (tool renderer). Renders when the tool part reaches
//      `output-available`. Shows the final summary, `extractedData`, and the
//      step log. Registered in `createDefaultToolRenderers()`.
//
// The tool renderer alone was not sufficient — Nova doesn't mount tool
// cards mid-execution reliably, so the live view wasn't appearing until the
// session was already over. The data-part renderer bypasses tool-state
// gating and renders as soon as the backend emits the first step event.

interface BrowserUseInput {
  task: string;
  startUrl?: string;
  successCriteria?: string;
}

/**
 * Data-part renderer for `data-browserStep`. Renders the live DCV stream
 * (or the current screenshot) as soon as the backend emits the first step
 * event — well before the tool call reaches `output-available`. Returns
 * `null` when there's no active step data yet so the message DOM stays
 * uncluttered for non-browserUse turns.
 *
 * Prefer `part`-scoped data over the global `useDataPart` value so multiple
 * `browserUse` calls in one conversation don't cross-contaminate (older card
 * showing a newer session's viewer URL). Falls back to the global value only
 * when the renderer is invoked without a `part` prop (e.g. legacy call sites).
 */
export function BrowserStepLiveView({ part }: DataPartRendererProps = {}): ReactElement | null {
  const [latestStep] = useDataPart<BrowserStepDataPart>('browserStep');
  const step = getDataPartData<BrowserStepDataPart>(part, 'browserStep') ?? latestStep;
  const preview = useBrowserPreviewSafe();
  if (!step) return null;
  // Backend emits `sessionClosed` right before releasing the AgentCore
  // session so the live-view stream unmounts BEFORE the presigned URL
  // dies. Also unmount on any terminal sub-agent action in case the
  // closed event was missed (e.g. stream aborted mid-flight).
  if (step.action === 'sessionClosed') return null;
  if (step.action === 'done' || step.action === 'giveUp') return null;
  // Missing viewer URL on a completed/failed status = session is done.
  if ((step.status === 'completed' || step.status === 'failed') && !step.sessionViewerUrl) return null;

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-gray-200 bg-white text-sm">
      <div className="flex items-center gap-2 px-3 py-2 text-gray-700">
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-blue-500" />
        <span className="font-medium">Browsing:</span>
        <span className="truncate text-gray-500">{step.actionSummary}</span>
        {preview ? (
          <button
            type="button"
            aria-label="Open browser preview in side panel"
            title="Enlarge"
            onClick={() =>
              preview.open({
                liveViewUrl: step.sessionViewerUrl,
                viewerType: step.viewerType,
                screenshotUrl: step.screenshotUrl,
                title: step.title || 'Browser session',
                subtitle: step.actionSummary || step.currentUrl,
              })
            }
            className="ml-auto flex size-6 shrink-0 items-center justify-center rounded-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <Maximize2 className="size-3.5" />
          </button>
        ) : null}
      </div>
      <div className="truncate border-t border-gray-100 px-3 py-2 text-xs text-gray-500">{step.currentUrl}</div>
      <div className="bg-gray-50 px-3 pb-3">
        {step.sessionViewerUrl && step.viewerType === 'dcv' ? (
          <div className="h-96 w-full overflow-hidden rounded border border-gray-200 bg-white">
            <BrowserLiveView signedUrl={step.sessionViewerUrl} />
          </div>
        ) : step.screenshotUrl ? (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt
          <img
            src={step.screenshotUrl}
            alt={step.actionSummary || 'Browser preview'}
            className="max-h-96 w-full border border-gray-200 bg-white object-contain"
          />
        ) : (
          <div className="py-4 text-center text-xs text-gray-500">Opening browser…</div>
        )}
      </div>
    </div>
  );
}

interface BrowserUseOutput {
  status: 'completed' | 'stopped_by_limit' | 'failed';
  summary: string;
  extractedData?: unknown;
  visitedUrls: string[];
  finalScreenshotUrl?: string;
  stepLog: { stepIndex: number; action: string; url?: string; summary: string }[];
  stopReason?: string;
}

export function BrowserUseTool({ part }: { part: ToolUIPart; messageUuid: string }) {
  const [latestStep] = useDataPart<BrowserStepDataPart>('browserStep');
  const [isExpanded, setIsExpanded] = useState(true);
  const preview = useBrowserPreviewSafe();

  const input = ((part as { input?: unknown }).input ?? {}) as BrowserUseInput;
  const output = (part as { output?: BrowserUseOutput }).output;
  const errorText = (part as { errorText?: string }).errorText;
  const isRunning = part.state !== 'output-available' && part.state !== 'output-error';
  const hasError = part.state === 'output-error';
  const task = input.task ?? '';

  // Only trust `latestStep` when it belongs to THIS tool call. With two
  // browserUse turns in a conversation, `useDataPart` returns the newest
  // session's step regardless of which card is rendering, so an older card
  // would otherwise iframe a stream that belongs to the newer session.
  const inputSessionId = (input as { sessionId?: string }).sessionId;
  const outputSessionId = output?.stepLog?.[0] && (output as unknown as { sessionId?: string }).sessionId;
  const stepSessionId = latestStep?.sessionId;
  // If we don't know the tool call's session id, and the tool is still
  // running, latestStep is very likely the right one — this is the common
  // path. Once output-available we prefer output data.
  const stepMatchesThisCall =
    !stepSessionId ||
    (isRunning && !inputSessionId && !outputSessionId) ||
    stepSessionId === inputSessionId ||
    stepSessionId === outputSessionId;
  // Ignore the terminal `sessionClosed` event when picking preview content —
  // it carries an empty screenshotUrl (the session is gone) which would
  // otherwise render as a broken image in the final summary card.
  const step = stepMatchesThisCall && latestStep?.action !== 'sessionClosed' ? latestStep : undefined;
  const previewUrl = step?.screenshotUrl || output?.finalScreenshotUrl;

  const statusColor = (() => {
    if (isRunning) return 'bg-blue-500';
    if (hasError) return 'bg-red-500';
    if (output?.status === 'completed') return 'bg-green-500';
    if (output?.status === 'stopped_by_limit') return 'bg-amber-500';
    return 'bg-red-500';
  })();

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white text-sm">
      <div className="flex w-full items-center gap-2">
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="flex flex-1 items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
        >
          <span className={`inline-block h-2 w-2 rounded-full ${statusColor} ${isRunning ? 'animate-pulse' : ''}`} />
          <span className="font-medium">
            {/* eslint-disable-next-line no-nested-ternary */}
            {isRunning ? 'Browsing' : hasError ? 'Browser failed' : 'Browsed'}: {task || '…'}
          </span>
          {isRunning && step ? <span className="truncate text-gray-500">— {step.actionSummary}</span> : null}
          {hasError && errorText ? <span className="truncate text-red-600">— {errorText}</span> : null}
          <span className="ml-auto text-xs text-gray-400">{isExpanded ? 'Hide' : 'Show'} preview</span>
        </button>
        {preview && (step?.sessionViewerUrl || previewUrl) ? (
          <button
            type="button"
            aria-label="Open browser preview in side panel"
            title="Enlarge"
            onClick={() =>
              preview.open({
                liveViewUrl: isRunning ? step?.sessionViewerUrl : undefined,
                viewerType: isRunning ? step?.viewerType : undefined,
                screenshotUrl: previewUrl,
                title: task ? `Browser: ${task}` : 'Browser session',
                subtitle: step?.actionSummary || step?.currentUrl || output?.summary,
              })
            }
            className="mr-2 flex size-7 shrink-0 items-center justify-center rounded-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <Maximize2 className="size-3.5" />
          </button>
        ) : null}
      </div>

      {isExpanded ? (
        <div className="border-t border-gray-100 bg-gray-50">
          <div className="truncate px-3 py-2 text-xs text-gray-500">
            {step?.currentUrl || output?.visitedUrls?.[output.visitedUrls.length - 1] || '—'}
          </div>
          <div className="px-3 pb-3">
            {/* eslint-disable-next-line no-nested-ternary */}
            {step?.sessionViewerUrl && step.viewerType === 'dcv' && isRunning ? (
              // AgentCore live stream via the vendored DCV Web Client SDK.
              // Rendered instead of the per-step PNG cascade while the
              // sub-agent is running, so the user sees the browser move in
              // real time. Screenshot fallback below still kicks in once
              // the session closes.
              <div className="h-96 w-full overflow-hidden rounded border border-gray-200 bg-white">
                <BrowserLiveView signedUrl={step.sessionViewerUrl} />
              </div>
            ) : previewUrl ? (
              <img
                src={previewUrl}
                alt={step?.actionSummary ?? 'Browser preview'}
                className="max-h-96 w-full border border-gray-200 bg-white object-contain"
              />
            ) : (
              <div className="py-4 text-center text-xs text-gray-500">Waiting for first screenshot…</div>
            )}
          </div>
          {hasError ? (
            <div className="space-y-1 border-t border-gray-100 px-3 py-2 text-xs">
              <div className="font-medium text-red-600">Browser tool failed</div>
              {errorText ? <div className="text-gray-700">{errorText}</div> : null}
            </div>
          ) : null}
          {!isRunning && !hasError && output ? (
            <div className="space-y-2 border-t border-gray-100 px-3 py-2">
              <div className="font-medium">{output.summary}</div>
              {output.stopReason ? <div className="text-xs text-gray-500">Stopped: {output.stopReason}</div> : null}
              {output.extractedData ? (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Extracted data</summary>
                  <pre className="mt-2 max-h-64 overflow-auto rounded border border-gray-200 bg-white p-2">
                    {JSON.stringify(output.extractedData, null, 2)}
                  </pre>
                </details>
              ) : null}
              {output.stepLog?.length ? (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                    Steps ({output.stepLog.length})
                  </summary>
                  <ol className="mt-2 space-y-1">
                    {output.stepLog.map((s) => (
                      <li key={s.stepIndex} className="text-gray-700">
                        {s.stepIndex}. {s.action}: {s.summary}
                      </li>
                    ))}
                  </ol>
                </details>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
